import type { GlobalAuditEntry } from '@/mocks/saGlobalAudit';
import { actionLabels, moduleLabels, moduleIcons, severityLabels } from '@/mocks/saGlobalAudit';

interface AuditTableProps {
  entries: GlobalAuditEntry[];
  onSelect: (entry: GlobalAuditEntry) => void;
}

const resultBadge: Record<string, string> = {
  success: 'bg-emerald-100 text-emerald-700',
  failure: 'bg-red-100 text-red-600',
  pending: 'bg-sky-100 text-sky-700',
};

const resultIcon: Record<string, string> = {
  success: 'ri-check-line',
  failure: 'ri-close-circle-line',
  pending: 'ri-time-line',
};

const resultLabel: Record<string, string> = {
  success: 'Éxito',
  failure: 'Fallido',
  pending: 'Pendiente',
};

function formatDate(dateStr: string): { date: string; time: string } {
  const parts = dateStr.split(' ');
  return { date: parts[0] ?? '', time: parts[1] ?? '' };
}

export default function AuditTable({ entries, onSelect }: AuditTableProps) {
  if (entries.length === 0) {
    return (
      <div className="bg-background-50 border border-background-200 rounded-2xl p-16 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
          <i className="ri-file-search-line text-foreground-300 text-2xl"></i>
        </div>
        <p className="text-foreground-800 text-sm font-semibold mb-1">Sin resultados</p>
        <p className="text-foreground-400 text-xs">No se encontraron registros con los filtros actuales.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Desktop table */}
      <div className="hidden lg:block bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px]">
            <thead>
              <tr className="border-b border-background-200 bg-background-100/50">
                {['Usuario', 'Acción', 'Módulo', 'Objetivo', 'Severidad', 'Fecha', 'IP', 'Resultado', ''].map((h) => (
                  <th key={h} className="text-left px-3 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => {
                const { date, time } = formatDate(entry.date);
                return (
                  <tr key={entry.id} className="border-b border-background-200/60 hover:bg-background-100/40 transition-colors">
                    <td className="px-3 py-3">
                      <p className="text-foreground-800 text-xs font-semibold">{entry.userName}</p>
                      <p className="text-foreground-400 text-[10px]">{entry.userRole}</p>
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-foreground-700 text-xs">{actionLabels[entry.action]}</span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-background-100 text-foreground-500 text-[10px] font-medium">
                        <i className={`${moduleIcons[entry.module]} text-[9px]`}></i>
                        {moduleLabels[entry.module]}
                      </span>
                    </td>
                    <td className="px-3 py-3 max-w-[200px]">
                      <p className="text-foreground-800 text-xs truncate">{entry.targetName}</p>
                      <p className="text-foreground-400 text-[10px] font-mono">{entry.targetId}</p>
                    </td>
                    <td className="px-3 py-3">
                      {entry.severity === 'info' && (
                        <span className="text-foreground-400 text-[10px]">{severityLabels[entry.severity]}</span>
                      )}
                      {entry.severity === 'warning' && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-medium">
                          <i className="ri-alert-line text-[9px]"></i>
                          {severityLabels[entry.severity]}
                        </span>
                      )}
                      {entry.severity === 'critical' && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 text-[10px] font-medium">
                          <i className="ri-error-warning-line text-[9px]"></i>
                          {severityLabels[entry.severity]}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <p className="text-foreground-500 text-xs">{date}</p>
                      <p className="text-foreground-300 text-[10px]">{time}</p>
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-foreground-500 text-[10px] font-mono">{entry.ipAddress}</span>
                    </td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${resultBadge[entry.result]}`}>
                        <i className={`${resultIcon[entry.result]} text-[9px]`}></i>
                        {resultLabel[entry.result]}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <button
                        onClick={() => onSelect(entry)}
                        className="w-8 h-8 rounded-lg bg-background-50 border border-background-200 flex items-center justify-center cursor-pointer hover:bg-background-100 hover:border-foreground-300 transition-all"
                      >
                        <i className="ri-eye-line text-foreground-500 text-sm"></i>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile cards */}
      <div className="lg:hidden space-y-2">
        {entries.map((entry) => {
          const { date, time } = formatDate(entry.date);
          return (
            <div key={entry.id} className="bg-background-50 border border-background-200 rounded-xl p-3">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <p className="text-foreground-800 text-xs font-semibold">{entry.userName}</p>
                  <p className="text-foreground-400 text-[10px]">{entry.userRole}</p>
                </div>
                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${resultBadge[entry.result]}`}>
                  <i className={`${resultIcon[entry.result]} text-[9px]`}></i>
                  {resultLabel[entry.result]}
                </span>
              </div>
              <div className="flex items-center gap-2 flex-wrap mb-1.5">
                <span className="text-foreground-700 text-[11px] font-medium">{actionLabels[entry.action]}</span>
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-background-100 text-foreground-500 text-[9px]">
                  <i className={`${moduleIcons[entry.module]} text-[8px]`}></i>
                  {moduleLabels[entry.module]}
                </span>
                {entry.severity !== 'info' && (
                  <span className={`text-[9px] font-medium ${entry.severity === 'critical' ? 'text-red-500' : 'text-amber-500'}`}>
                    {severityLabels[entry.severity]}
                  </span>
                )}
              </div>
              <p className="text-foreground-600 text-[10px] truncate mb-1.5">{entry.targetName}</p>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-foreground-400 text-[10px]">{date} {time}</span>
                  <span className="text-foreground-300 text-[9px] ml-2 font-mono">{entry.ipAddress}</span>
                </div>
                <button
                  onClick={() => onSelect(entry)}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg bg-background-100 text-foreground-500 text-[10px] font-medium hover:bg-background-200 transition-colors cursor-pointer"
                >
                  <i className="ri-eye-line text-[9px]"></i>
                  Ver
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}